import Event from '../../classes/event';
import Pages from '@pages';
import { IRouteState } from '../../types/interfaces/states';
import { RootState } from '../store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: IRouteState = {
  route: {
    Page: Pages.INITIAL,
    stack: [],
  },
  params: {
    current: undefined,
    stack: [],
  },
  currentAsset: null,
};

const routerSlice = createSlice({
  name: 'router',
  initialState,
  reducers: {
    clearStack: (state) => {
      state.params.stack = [];
      state.route.stack = [];
    },
    emptyRoute: (state) => {
      state.params.current = { message: '' };
      state.params.stack = [];
      state.route.Page = Pages.EMPTY;
    },
    popRoute: (state) => {
      if (state.route.stack.length) {
        const NewPage = state.route.stack.pop();
        const newParams = state.params.stack.pop();
        if (NewPage) {
          state.route.Page = NewPage;
          state.params.current = newParams as { [key: string]: unknown };
        }
      }
    },
    pushRoute: (
      state,
      action: PayloadAction<{
        params?: {
          current?: {
            [key: string]: unknown;
          };
          stack?: {
            [key: string]: unknown;
          };
          push?: boolean;
        };
        route: {
          push?: boolean;
          overwrite?: boolean;
          Page: () => JSX.Element;
        };
      }>
    ) => {
      const { params, route } = action.payload;
      const {
        params: { current: currentParams },
      } = state;

      // Either create a new route or push it into the stack

      if (route.overwrite) {
        state.params.current = undefined;
        state.params.stack = [];
        state.route.stack = [];
      } else {
        route?.push !== false && state.route.stack.push(state.route.Page);
      }

      // Replace route and its params

      state.route.Page = route.Page;
      state.params.current = params?.current;

      // Stack any required params

      let newParams:
        | {
            [x: string]: unknown;
          }
        | undefined = {
        ...(params?.push && currentParams),
        ...params?.stack,
      };
      !(Object.keys(newParams).length > 0) && (newParams = undefined);
      newParams && state.params.stack.push(newParams);
    },
    setParams: (
      state,
      action: PayloadAction<{
        [key: string]: unknown;
      }>
    ) => {
      state.params.current = action.payload;
    },
    updateParams: (
      state,
      action: PayloadAction<{
        [key: string]: unknown;
      }>
    ) => {
      state.params.current = { ...state.params.current, ...action.payload };
    },
    updateStack: (
      state,
      action: PayloadAction<{
        index: number;
        params: {
          [key: string]: unknown;
        };
      }>
    ) => {
      if (
        state.params.stack?.length &&
        action.payload.index < state.params.stack.length
      ) {
        state.params.stack[action.payload.index] = action.payload.params;
      }
    },
    setCurrentAsset: (state, action: PayloadAction<Event | null>) => {
      state.currentAsset = action.payload;
    },
    clearCurrentAsset: (state) => {
      state.currentAsset = null;
    },
  },
});

const { actions, reducer: routerReducer } = routerSlice;
export const {
  clearStack,
  emptyRoute,
  popRoute,
  pushRoute,
  setParams,
  updateParams,
  updateStack,
  setCurrentAsset,
  clearCurrentAsset,
} = actions;
export const routerSelector = (state: RootState) => state.router;
/**
 * Scoped selectors so hot-path consumers don't re-render on unrelated slice
 * changes — Immer gives this slice a new reference on any nested-field
 * change, so the whole-slice selector re-renders even when a component
 * reads only one field.
 */
export const selectRoute = (state: RootState) => state.router.route;
export const selectRoutePage = (state: RootState) => state.router.route.Page;
export const selectCurrentParams = (state: RootState) =>
  state.router.params.current;
export const selectParamsStack = (state: RootState) =>
  state.router.params.stack;
export { initialState };
export default routerReducer;
